// The location state of the links between the pages of the News area: a
// page remembers where it was opened from (`from`: pathname and query
// string, the way back) and, one step further, where that place came from
// (`back`), so that a page two steps away keeps its own way back: from the
// sittings (L1) to a sitting (L2) to a document (L3) and back to the
// sitting, «Torna alle sedute» still leads to the view of L1.

/** The state of a link to a page of the area: where it was opened from, and where that place came from. */
export interface PageLinkState {
  from: string;
  back?: string;
}

/** The `from` of a location state, when it carries one. */
export function fromOf(state: unknown): string | null {
  // `in` narrows `state` to an object with the key, typed unknown: no cast
  if (typeof state === "object" && state !== null && "from" in state && typeof state.from === "string")
    return state.from;
  return null;
}

/**
 * The state of a link from `location` to another page: the location as
 * `from`; its own `from`, when it has one, as `back`.
 */
export function pageLinkState(location: { pathname: string; search: string; state?: unknown }): PageLinkState {
  const from = `${location.pathname}${location.search}`;
  const back = fromOf(location.state);
  return back ? { from, back } : { from };
}

/**
 * The state of the link back from a page: the `back` of its state as the
 * `from` of the page it returns to, so that page keeps its own way back;
 * nothing when the state carries no `back`.
 */
export function backLinkState(state: unknown): { from: string } | undefined {
  if (typeof state === "object" && state !== null && "back" in state && typeof state.back === "string")
    return { from: state.back };
  return undefined;
}
